import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { ExpandLess, ExpandMore, Timeline } from '@mui/icons-material';
import 'dayjs/locale/es';
import {
  Box,
  CircularProgress,
  Collapse,
  Skeleton,
  Stack,
  Typography
} from '@mui/material';

import { useGetEmailThread } from '@frontend/features/cases/queries';
import {
  ThreadToggleButton,
  ThreadContainer,
  ThreadHeader,
  ThreadEmailSeparator,
  ThreadEmailItem,
  ThreadAvatar,
  ThreadEmailContent,
  ThreadDateBadge
} from '../styled';

interface EmailThreadProps {
  emailId: number;
  isPartOfThread: boolean;
}

export const EmailThread = ({ emailId, isPartOfThread }: EmailThreadProps) => {
  const { t } = useTranslation();
  const [showFullThread, setShowFullThread] = useState(false);

  const {
    data: threadEmails = [],
    isLoading: loadingThread
  } = useGetEmailThread({
    emailId,
    enabled: showFullThread && isPartOfThread,
    onErrorCallback: (error) => {
      console.error('Error loading thread:', error);
    }
  });


  const loadFullThread = () => {
    setShowFullThread(!showFullThread);
  };

  if (!isPartOfThread) {
    return null;
  }

  return (
    <>
      <Box mt={1}>
        <ThreadToggleButton
          size="small"
          variant="text"
          onClick={loadFullThread}
          disabled={loadingThread}
          startIcon={
            loadingThread ? <CircularProgress size={16} color="inherit" /> :
              showFullThread ? <ExpandLess /> : <ExpandMore />
          }
        >
          {loadingThread
            ? t('cases.view.content.loading')
            : showFullThread
              ? t('cases.view.content.hideThread')
              : threadEmails.length > 0
                ? t('cases.view.content.showThreadWithCount', { count: threadEmails.length })
                : t('cases.view.content.showThread')
          }
        </ThreadToggleButton>
      </Box>

      <Collapse
        sx={{
          maxWidth: 700,
        }}
        in={showFullThread}>
        <>
          {
            loadingThread ? <Skeleton variant="rounded" height={100} /> : (
              <ThreadContainer>
                <ThreadHeader
                  variant="subtitle2"
                  color="text.secondary"
                >
                  <Timeline /> {t('cases.view.content.previousMessages', { count: threadEmails.length })}
                </ThreadHeader>

                <Stack spacing={0}>
                  {threadEmails.map((email, index) => (
                    <Box key={email.id}>
                      {index > 0 && <ThreadEmailSeparator />}

                      <ThreadEmailItem>
                        <ThreadAvatar>
                          {email.from?.charAt(0)?.toUpperCase()}
                        </ThreadAvatar>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Typography
                              variant="subtitle2"
                              color="text.primary"
                            >
                              {email.from}
                            </Typography>

                            <ThreadDateBadge
                              variant="caption"
                              color="text.secondary"
                            >
                              {email.created_at ?
                                dayjs(email.created_at).format('DD/MM/YY HH:mm') :
                                t('cases.view.content.noDate')
                              }
                            </ThreadDateBadge>


                          </Box>

                          <ThreadEmailContent>
                            <div dangerouslySetInnerHTML={{
                              __html: email.original_content || email.original_text || email.content
                            }} />
                          </ThreadEmailContent>

                        </Box>
                      </ThreadEmailItem>
                    </Box>
                  ))}
                </Stack>
              </ThreadContainer>
            )
          }
        </>
      </Collapse>
    </>
  );
};
